/* ============================================================
   Shabbat Mode — לוגיקת זיהוי, החלפת כפתורים, ופופאפ
   ============================================================
   למה הכל בקובץ אחד:
   הפיצ'ר מבוסס על מצב יחיד (Shabbat active/inactive) ש"מצביע"
   על כמה אזורי DOM שונים. הפרדה לקבצים קטנים תייצר תיאום מורכב,
   ויעבור לטעון יותר רכיבים בזבזניים.

   זרימה:
   1. בעת DOMContentLoaded — קוראים את window.shabbatConfig
   2. בודקים האם זמן ישראל הנוכחי נמצא בתוך אחת התקופות, או override ב-URL
   3. אם פעיל — מוסיפים body.shabbat-active. CSS מטפל בהסתרות.
   4. מזריקים הודעת "אתר שומר שבת" לבר ההודעות
   5. ליד כל כפתור הוספה-לעגלה מזריקים כפתור תזכורת
   6. כפתור התזכורת פותח פופאפ עם טופס Shopify ליצירת לקוח
   7. שליחת הטופס יוצרת לקוח בשופיפיי + שולחת ל-Make webhook
============================================================ */

(function () {
  'use strict';

  const STATE = {
    active: false,
    label: null,
    productContext: null,
  };

  const cfg = window.shabbatConfig || { enabled: false, closures: [], webhookUrl: '', ui: {} };
  const ui = cfg.ui || {};
  const T = {
    announcement: ui.announcementText || 'אתר שומר שבת 🕯️',
    reminderBtn: ui.reminderBtnText || 'תזכרו אותי במוצ"ש 🕯️',
    toastCart: ui.toastCart || 'אתר שומר שבת — לא ניתן לעבור לעגלה כעת 🕯️',
    toastCheckout: ui.toastCheckout || 'אתר שומר שבת — לא ניתן להזמין כעת 🕯️',
    thanks: ui.thanksText || 'תודה! 🕯️ נחזור אליך במוצ"ש עם תזכורת.',
  };

  /* -------- 1. זיהוי זמן -------- */

  // ממיר Date לאובייקט {year, month, day, hour, minute} באזור זמן ישראל
  function getJerusalemParts(date) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Jerusalem',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = {};
    fmt.formatToParts(date).forEach((p) => (parts[p.type] = p.value));
    return {
      year: parseInt(parts.year, 10),
      month: parseInt(parts.month, 10),
      day: parseInt(parts.day, 10),
      hour: parseInt(parts.hour, 10),
      minute: parseInt(parts.minute, 10),
    };
  }

  // ממיר {date, time} ל"מספר השוואה" YYYYMMDDHHMM. בלי tz — שני הצדדים בישראל.
  function toComparable(dateStr, timeStr) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map((s) => parseInt(s, 10));
    const [hh, mm] = (timeStr || '00:00').split(':').map((s) => parseInt(s, 10));
    if (isNaN(y) || isNaN(m) || isNaN(d) || isNaN(hh) || isNaN(mm)) return null;
    return y * 100000000 + m * 1000000 + d * 10000 + hh * 100 + mm;
  }

  function getShabbatStatus() {
    // URL override — לבדיקות
    const params = new URLSearchParams(window.location.search);
    const force = params.get('force_shabbat');
    if (force === 'true') return { isActive: true, label: 'מצב בדיקה' };
    if (force === 'false') return { isActive: false, label: null };

    if (!cfg.enabled) return { isActive: false, label: null };
    if (!Array.isArray(cfg.closures) || cfg.closures.length === 0) {
      return { isActive: false, label: null };
    }

    const now = getJerusalemParts(new Date());
    const nowCmp =
      now.year * 100000000 +
      now.month * 1000000 +
      now.day * 10000 +
      now.hour * 100 +
      now.minute;

    for (const c of cfg.closures) {
      const start = toComparable(c.startDate, c.startTime);
      const end = toComparable(c.endDate, c.endTime);
      if (start && end && nowCmp >= start && nowCmp <= end) {
        return { isActive: true, label: c.label || null };
      }
    }
    return { isActive: false, label: null };
  }

  /* -------- 2. הזרקת הודעה לבר ההודעות -------- */

  function injectAnnouncement() {
    if (document.querySelector('[data-shabbat-announcement]')) return;

    const msg = document.createElement('div');
    msg.setAttribute('data-shabbat-announcement', '');
    msg.className = 'shabbat-announcement';
    const span = document.createElement('span');
    span.className = 'shabbat-announcement__text';
    span.textContent = T.announcement;
    msg.appendChild(span);

    // Horizon: כל ה-header (אנונסמנט + הדר) יושב ב-#header-group. מזריקים את
    // ההודעה לראש ה-group כך שתופיע מעל הכל; ה-CSS מסתיר את האנונסמנט הרגיל.
    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      headerGroup.insertBefore(msg, headerGroup.firstChild);
      return;
    }
    // נפילה: לפני ה-header אם אין group
    const header = document.querySelector('#header-component, .header, header');
    if (header) {
      header.parentNode.insertBefore(msg, header);
      return;
    }
    // fallback אחרון: ראש ה-body
    document.body.insertBefore(msg, document.body.firstChild);
  }

  /* -------- 3. החלפת טפסי הוספה-לעגלה + חסימת עגלה -------- */

  // למה form-based ולא button-based:
  // כל theme/page-builder (Dawn, EComposer, מותאם אישית) מוציא ATC כ-
  // <form action="/cart/add">. זה הסטנדרט של שופיפיי. עטיפה ברמת הטופס
  // עובדת גם כשhasButton class אקראית או web-component עושה rerender.

  function cleanUrl(rawUrl) {
    // מנקה query params פנימיים שלא רלוונטיים ללקוח (כמו force_shabbat לבדיקות),
    // ומשאיר את שאר ה-URL נקי לשליחה ב-WhatsApp.
    try {
      const u = new URL(rawUrl);
      u.searchParams.delete('force_shabbat');
      u.searchParams.delete('shabbat_thanks');
      const qs = u.searchParams.toString();
      return u.origin + u.pathname + (qs ? '?' + qs : '') + u.hash;
    } catch {
      return rawUrl;
    }
  }

  function extractFormContext(form) {
    const idInput = form.querySelector('input[name="id"]');
    return {
      variantId: idInput?.value || null,
      url: cleanUrl(window.location.href),
      cartLevel: false,
    };
  }

  function makeReminderBtn(getContext) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'shabbat-reminder-btn';
    const span = document.createElement('span');
    span.textContent = T.reminderBtn;
    btn.appendChild(span);
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      STATE.productContext = getContext();
      openPopup();
    });
    return btn;
  }

  function wrapAtcForm(form) {
    if (form.dataset.shabbatWrapped === '1') return;
    form.dataset.shabbatWrapped = '1';

    // ה-ATC הסטנדרטי של Shopify הוא button[name="add"]. רק אותו וכפתורי
    // express checkout מסתירים — לא quantity ולא variant pickers.
    let atcButtons = form.querySelectorAll(
      'button[name="add"], input[name="add"], [data-type="payment-button"]',
    );

    // fallback — סקציות מותאמות כמו speedate-v3 לא מציבות name="add".
    // אם לא מצאנו ATC סטנדרטי, ניקח את ה-submit האחרון בטופס (אחרי כל ה-variants).
    if (atcButtons.length === 0) {
      const submits = form.querySelectorAll('button[type="submit"], input[type="submit"]');
      if (submits.length > 0) {
        atcButtons = [submits[submits.length - 1]];
      }
    }

    let anchor = null;
    atcButtons.forEach((b) => {
      b.dataset.shabbatHidden = '1';
      b.style.setProperty('display', 'none', 'important');
      if (!anchor) anchor = b;
    });

    if (!anchor) {
      form.dataset.shabbatWrapped = '0';
      return;
    }

    const reminder = makeReminderBtn(() => extractFormContext(form));
    anchor.insertAdjacentElement('afterend', reminder);

    // חוסם submission של הטופס לחלוטין כשהמצב פעיל
    form.addEventListener(
      'submit',
      (e) => {
        if (!STATE.active) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        STATE.productContext = extractFormContext(form);
        openPopup();
      },
      true,
    );
  }

  // כפתורי הוספה-לסל מבוססי-JS של התמה החדשה (לא <form>): כפתור הבאנדל ב-PDP
  // מוסיף לסל דרך fetch('/cart/add.js'), אז ה-form-wrapping לא תופס אותו —
  // מסתירים אותו ומזריקים כפתור תזכורת במקומו (כמו ה-ATC הרגיל).
  function wrapCustomAddBtn(btn) {
    if (btn.dataset.shabbatWrapped === '1') return;
    btn.dataset.shabbatWrapped = '1';
    btn.dataset.shabbatHidden = '1';
    btn.style.setProperty('display', 'none', 'important');
    // כפתור באנדל מוסיף כמה וריאנטים — אין variant יחיד, נשאיר null (הליד לא תלוי בו)
    const reminder = makeReminderBtn(() => ({
      variantId: null,
      url: cleanUrl(window.location.href),
      cartLevel: false,
    }));
    btn.insertAdjacentElement('afterend', reminder);
  }

  function scanAndWrap(root = document) {
    // כל טופס שמוסיף לעגלה — הסטנדרט של שופיפיי
    root.querySelectorAll?.('form[action*="/cart/add"]').forEach(wrapAtcForm);
    // כפתורי הוספה מבוססי-JS שלנו
    root.querySelectorAll?.('.kapel-pdp__bundle-cta').forEach(wrapCustomAddBtn);
  }

  // חוסם פתיחת cart drawer ומעבר ל-checkout
  function installCartBlocker() {
    // סלקטורים נפוצים של פותחי drawer / לינקים לעגלה
    const CART_TRIGGER_SEL = [
      'a[href$="/cart"]',
      'a[href*="/cart?"]',
      '#cart-icon-bubble',
      '[data-cart-trigger]',
      '[aria-controls*="cart-drawer"]',
      '[aria-controls*="CartDrawer"]',
      'cart-icon-bubble',
      '.header__icon--cart',
      /* Horizon / התמה החדשה: אייקון העגלה שפותח את ה-cart-drawer-component */
      'cart-icon',
      '.header-actions__cart-icon',
    ].join(',');

    document.addEventListener(
      'click',
      (e) => {
        if (!STATE.active) return;
        const trigger = e.target.closest?.(CART_TRIGGER_SEL);
        if (trigger && !trigger.classList.contains('shabbat-reminder-btn')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showToast(T.toastCart);
          return;
        }
        // חוסם גם כפתורי checkout
        const checkoutBtn = e.target.closest?.(
          'button[name="checkout"], a[href="/checkout"], a[href$="/checkout"], .cart__checkout-button',
        );
        if (checkoutBtn) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showToast(T.toastCheckout);
        }
      },
      true,
    );

    // submit-level blocker — שכבת ביטחון נוספת
    document.addEventListener(
      'submit',
      (e) => {
        if (!STATE.active) return;
        const form = e.target;
        if (form?.action?.match(/\/(cart\/add|checkout)/)) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true,
    );
  }

  function showToast(text) {
    let t = document.querySelector('.shabbat-toast');
    if (t) t.remove();
    t = document.createElement('div');
    t.className = 'shabbat-toast';
    t.textContent = text;
    document.body.appendChild(t);
    requestAnimationFrame(() => t.classList.add('is-visible'));
    setTimeout(() => t.classList.remove('is-visible'), 2400);
    setTimeout(() => t.remove(), 2900);
  }

  /* -------- 4. פופאפ + טופס -------- */

  let popupEl = null;

  function buildPopup() {
    const tpl = document.getElementById('shabbat-popup-template');
    if (!tpl) {
      console.warn('[shabbat-mode] popup template missing');
      return null;
    }
    const node = tpl.content.firstElementChild.cloneNode(true);
    document.body.appendChild(node);

    // סגירה — שני כפתורי close (X למעלה, "סגור" למטה) + לחיצה על הoverlay
    node.querySelectorAll('[data-shabbat-close]').forEach((el) =>
      el.addEventListener('click', closePopup),
    );
    node.querySelector('.shabbat-popup__overlay')?.addEventListener('click', closePopup);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && node.classList.contains('is-open')) closePopup();
    });

    // intercept submit כדי לשלוח גם ל-Make
    const form = node.querySelector('form');
    if (form) form.addEventListener('submit', handleFormSubmit);

    return node;
  }

  function openPopup() {
    if (!popupEl) popupEl = buildPopup();
    if (!popupEl) return;
    document.body.classList.add('shabbat-popup-open');
    requestAnimationFrame(() => {
      popupEl.classList.add('is-open');
      popupEl.querySelector('input[name="customer[first_name]"]')?.focus();
    });
  }

  function closePopup() {
    if (!popupEl) return;
    popupEl.classList.remove('is-open');
    document.body.classList.remove('shabbat-popup-open');
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const consentErr = form.querySelector('[data-shabbat-consent-error]');
    const networkErr = form.querySelector('[data-shabbat-network-error]');
    const submitBtn = form.querySelector('.shabbat-popup__submit');
    const submitText = form.querySelector('.shabbat-popup__submit-text');
    const submitSpin = form.querySelector('.shabbat-popup__submit-spinner');

    // ולידציה — הסכמה לדיוור חובה (חוק ישראלי)
    const consent = form.querySelector('input[name="accepts_marketing"]');
    if (consent && !consent.checked) {
      if (consentErr) consentErr.hidden = false;
      consent.focus();
      return;
    }
    if (consentErr) consentErr.hidden = true;
    if (networkErr) networkErr.hidden = true;

    if (!cfg.webhookUrl) {
      console.warn('[shabbat-mode] webhook URL not configured');
      if (networkErr) networkErr.hidden = false;
      return;
    }

    // לוקינג כפתור
    if (submitBtn) submitBtn.disabled = true;
    if (submitText) submitText.hidden = true;
    if (submitSpin) submitSpin.hidden = false;

    const data = collectFormData(form);

    try {
      const resp = await fetch(cfg.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive: true,
      });
      // Make מחזיר 200 גם בהצלחה — בודקים רק ש-status בטווח 2xx
      if (!resp.ok) throw new Error('webhook returned ' + resp.status);

      // סגירה + הודעת תודה
      closePopup();
      showThanksBanner();
    } catch (err) {
      console.warn('[shabbat-mode] webhook failed', err);
      if (networkErr) networkErr.hidden = false;
    } finally {
      if (submitBtn) submitBtn.disabled = false;
      if (submitText) submitText.hidden = false;
      if (submitSpin) submitSpin.hidden = true;
    }
  }

  function showThanksBanner() {
    const banner = document.createElement('div');
    banner.className = 'shabbat-thanks-banner';
    banner.textContent = T.thanks;
    document.body.appendChild(banner);
    setTimeout(() => banner.classList.add('is-fading'), 3500);
    setTimeout(() => banner.remove(), 4500);
  }

  function collectFormData(form) {
    const fd = new FormData(form);
    const ctx = STATE.productContext || {};
    return {
      first_name: (fd.get('first_name') || '').toString().trim(),
      last_name: (fd.get('last_name') || '').toString().trim(),
      email: (fd.get('email') || '').toString().trim().toLowerCase(),
      phone: (fd.get('phone') || '').toString().trim(),
      accepts_marketing: fd.get('accepts_marketing') === '1' || fd.get('accepts_marketing') === 'on',
      variant_id: ctx.variantId || null,
      page_url: ctx.url || cleanUrl(window.location.href),
      cart_level: !!ctx.cartLevel,
      closure_label: STATE.label,
      shop_domain: window.location.hostname,
      timestamp: new Date().toISOString(),
    };
  }

  /* -------- 5. אתחול -------- */

  function activate() {
    document.body.classList.add('shabbat-active');
    injectAnnouncement();
    scanAndWrap(document);

    // צופה ב-DOM לתוכן דינמי + לrerender שמשחזר submits מוסתרים
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.('form[action*="/cart/add"]')) wrapAtcForm(n);
          scanAndWrap(n);
        });
        // אם submit שכבר הסתרנו "נפתח" שוב (style השתנה) — נחזיר למצב מוסתר
        if (m.type === 'attributes' && m.attributeName === 'style') {
          const t = m.target;
          if (t.dataset?.shabbatHidden === '1' && t.style.display !== 'none') {
            t.style.setProperty('display', 'none', 'important');
          }
        }
      }
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  function init() {
    const status = getShabbatStatus();
    STATE.active = status.isActive;
    STATE.label = status.label;
    if (STATE.active) activate();
    installCartBlocker(); // בכל מצב — חוסם בזמן שבת בלבד אבל מותקן תמיד
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // נחשף לבדיקות בקונסולה
  window.getShabbatStatus = getShabbatStatus;
})();
